import { useState, useEffect } from 'react'
import {
  FlexGrid,
  ButtonLink,
  PriceLockup,
  Divider,
  Typography,
  Spacer,
  TypographyProps,
} from '@telus-uds/components-web'
import Toggle from './Toggle'
import { LegalTextRenderer } from '../../../utils/text/TextRenderer'

import useLocalStorage from '../../../utils/useLocalStorage'
import {
  TypeIconWithoutUnresolvableLinksResponse,
  TypePriceLockupWithoutUnresolvableLinksResponse,
} from 'contentful-types'
import { PriceLockupWrapper, PriceToggleWrapper } from './Styles'

export type PriceToggleProps = {
  priceLockupCoupon?: TypePriceLockupWithoutUnresolvableLinksResponse['fields']
  priceLockupNoCoupon?: TypePriceLockupWithoutUnresolvableLinksResponse['fields']
  ogPriceWithCoupon?: number
  ogPriceNoCoupon?: number
  priceA11y?: string
  toggleLabel?: string
  toggleLabelBold?: string
  toggleA11y: string
  tooltipText?: string
  toggleActive: boolean
  buttonText?: string
  buttonUrlActive?: string
  buttonUrlInactive?: string
  buttonVariant?: 'high' | 'low'
  buttonAlign?: 'start' | 'center' | 'end'
  icon?: TypeIconWithoutUnresolvableLinksResponse
  localStorageKey: string
  priceLockupGutter?: boolean
  limitWidth?: boolean
}

const PriceToggle = ({
  priceLockupCoupon = undefined,
  priceLockupNoCoupon = undefined,
  ogPriceWithCoupon,
  ogPriceNoCoupon,
  priceA11y,
  toggleLabel,
  toggleLabelBold,
  toggleA11y,
  tooltipText = '',
  toggleActive,
  buttonText = '',
  buttonUrlActive,
  buttonUrlInactive,
  buttonVariant = 'high',
  buttonAlign = 'start',
  icon,
  localStorageKey,
  priceLockupGutter = true,
  limitWidth = true,
}: PriceToggleProps) => {
  const [active, setActive] = useLocalStorage(localStorageKey, toggleActive)
  const [url, setUrl] = useState(toggleActive ? buttonUrlActive : buttonUrlInactive)
  const [priceLockupDetails, setPriceLockupDetails] = useState(
    toggleActive ? priceLockupCoupon || {} : priceLockupNoCoupon || {}
  )
  const [ogPrice, setOgPrice] = useState(toggleActive ? ogPriceWithCoupon : ogPriceNoCoupon)

  useEffect(() => {
    setUrl(active ? buttonUrlActive : buttonUrlInactive)
    setPriceLockupDetails(active ? priceLockupCoupon || {} : priceLockupNoCoupon || {})
    setOgPrice(active ? ogPriceWithCoupon : ogPriceNoCoupon)
  }, [active])

  const handleToggle = () => {
    setActive(!active)
    window.dispatchEvent(new Event('priceToggleEvent'))
  }

  const withLegal = (text) => {
    return text ? <LegalTextRenderer>{text}</LegalTextRenderer> : ''
  }

  const {
    topText,
    price,
    signDirection = 'left',
    rateText,
    bottomText,
    bottomTextAlignment = 'left',
  } = priceLockupDetails as TypePriceLockupWithoutUnresolvableLinksResponse['fields']

  const bottomTextAlign = bottomTextAlignment.toLowerCase() as TypographyProps['align']

  return (
    <PriceToggleWrapper data-testid="price-toggle-testid" limitWidth={limitWidth}>
      {priceLockupCoupon && priceLockupNoCoupon && (
        <FlexGrid data-testid="lockups" gutter={priceLockupGutter}>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <PriceLockupWrapper>
                <PriceLockup
                  size="medium"
                  topText={withLegal(topText)}
                  price={price}
                  signDirection={signDirection.toLowerCase()}
                  rateText={withLegal(rateText)}
                />
                {ogPrice && (
                  <PriceLockup
                    size="small"
                    strikeThrough
                    a11yText={priceA11y}
                    price={ogPrice}
                    signDirection={signDirection.toLowerCase()}
                    rateText={withLegal(rateText)}
                  />
                )}
              </PriceLockupWrapper>
            </FlexGrid.Col>
          </FlexGrid.Row>
          {bottomText && (
            <>
              <Divider tokens={{ color: '#E5E5E5' }} space={{ xs: 2, md: 3 }} />
              <Typography block align={bottomTextAlign} variant={{ size: 'small' }}>
                {withLegal(bottomText)}
              </Typography>
            </>
          )}
          <Spacer space={4} />
        </FlexGrid>
      )}
      <Toggle
        label={toggleLabel}
        labelBold={toggleLabelBold}
        a11y={toggleA11y}
        tooltipText={tooltipText}
        active={active}
        icon={icon}
        onToggle={handleToggle}
      />

      {buttonText && url && (
        <>
          <Spacer space={4} />
          <FlexGrid>
            <FlexGrid.Row horizontalAlign={buttonAlign}>
              <ButtonLink href={url} variant={{ priority: buttonVariant }}>
                {buttonText}
              </ButtonLink>
            </FlexGrid.Row>
          </FlexGrid>
        </>
      )}
    </PriceToggleWrapper>
  )
}

export default PriceToggle
